/**
 * KioskLiveView — real-time "who's on the clock" dashboard across all
 * of the company's kiosks (Jun 2026). Opened from Manage Teams.
 * Auto-refreshes every 30 seconds. GET /api/kiosks/live
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Radio, MapPin, Loader2, RefreshCw, MonitorSmartphone } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtSince = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

export default function KioskLiveView({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/kiosks/live`, { headers: authHeaders() });
      setData(r.data);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Could not load kiosk live view');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [open, load]);

  const kiosks = data?.kiosks || [];
  const totalOn = kiosks.reduce((a, k) => a + k.members.filter((m) => m.clocked_in).length, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="kiosk-live-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-emerald-400" />
            <span>Kiosk Live View</span>
            {data && (
              <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,200,150,0.12)', color: '#00C896', border: '1px solid rgba(0,200,150,0.4)' }} data-testid="kiosk-live-total">
                {totalOn} on the clock
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between gap-2">
            <span>Live status across all kiosks — refreshes every 30 seconds.</span>
            <Button size="sm" variant="outline" onClick={() => load()} disabled={loading} data-testid="kiosk-live-refresh-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </DialogDescription>
        </DialogHeader>

        {!data && loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : kiosks.length === 0 ? (
          <div className="text-center py-12" data-testid="kiosk-live-empty">
            <MonitorSmartphone className="w-10 h-10 mx-auto mb-3" style={{ color: '#2D3748' }} />
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              No kiosks yet — use the "Kiosk" button next to a team to set one up.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {kiosks.map((k) => {
              const onClock = k.members.filter((m) => m.clocked_in);
              return (
                <div
                  key={k.kiosk_id}
                  className="rounded-xl p-4"
                  style={{ background: '#0F1923', border: `1px solid ${k.enabled ? '#2D3748' : 'rgba(239,68,68,0.35)'}` }}
                  data-testid={`kiosk-live-card-${k.kiosk_id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{k.team_name}</p>
                      <p className="text-xs flex items-center gap-1" style={{ color: '#64748B' }}>
                        <MapPin className="w-3 h-3" /> {k.location_label || 'No location set'}
                        {!k.enabled && <span className="ml-2 text-red-400 font-semibold">· Kiosk disabled</span>}
                      </p>
                    </div>
                    <span className="text-xs font-bold" style={{ color: onClock.length > 0 ? '#00C896' : '#64748B' }}>
                      {onClock.length}/{k.members.length} on the clock
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {k.members.map((m) => (
                      <span
                        key={m.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
                        style={{
                          background: m.clocked_in ? 'rgba(0,200,150,0.1)' : '#1A2433',
                          color: m.clocked_in ? '#00C896' : '#94A3B8',
                          border: `1px solid ${m.clocked_in ? 'rgba(0,200,150,0.45)' : '#2D3748'}`,
                        }}
                        data-testid={`kiosk-live-member-${m.id}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${m.clocked_in ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'}`}
                        />
                        {m.name}
                        {m.clocked_in && <span style={{ color: '#5EEAD4' }}>· since {fmtSince(m.clock_in_time)}</span>}
                      </span>
                    ))}
                    {k.members.length === 0 && (
                      <span className="text-xs" style={{ color: '#64748B' }}>No team members.</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
